<script setup lang="ts">
  import { MpCard, MpText, useMpScreen } from '@borg/ui';
  import { injectAuthForm } from '@/components/Auth/useAuthForm';

  const images = useImages();
  const authForm = injectAuthForm();
  const siteTheme = useSiteTheme();
  const { breakpoints } = useMpScreen();
  const authContainer = ref();
  const direction = ref<'right' | 'left'>();

  function tabToImage(tab: (typeof authForm)['config']['tab']) {
    if (tab === 'login' || tab === 'register') {
      return tab;
    } else if (tab === 'forgotten_password') {
      return 'forgotten-password';
    } else {
      return 'login';
    }
  }

  const currentImage = computed(() =>
    authForm.config.tab
      ? images[tabToImage(authForm.config.tab)]
        ? `url('${images[tabToImage(authForm.config.tab)].default[siteTheme.theme === 'dark' ? 'desktopDark' : 'desktopLight']}')`
        : 'none'
      : 'none',
  );
  const pastImage = computed(() =>
    authForm.config.previousTab
      ? images[tabToImage(authForm.config.previousTab)]
        ? `url('${images[tabToImage(authForm.config.previousTab)].default[siteTheme.theme === 'dark' ? 'desktopDark' : 'desktopLight']}')`
        : 'none'
      : 'none',
  );

  watch(
    () => authForm.config.tab,
    (tab) => (direction.value = tab === 'login' ? 'right' : 'left'),
  );
</script>

<template>
  <div
    ref="authContainer"
    :class="{
      'container': true,
      [`container__${authForm.config.tab}`]: true,
      [`container--${direction}`]: !!direction,
    }"
  >
    <MpCard
      class="container__form"
      :p="!breakpoints.isMd ? 'sm' : 'lg'"
      :border="breakpoints.isMd"
      :shadow="breakpoints.isMd"
    >
      <slot />
    </MpCard>
    <MpText>
      <Copyright />
    </MpText>
  </div>
</template>

<style scoped lang="scss">
  $positionLeft: top 142px right calc(50% + 360px);
  $positionLeftCenter: top 50px right calc(50% + 100px);
  $positionCenter: top 30px right 50%;
  $positionRightCenter: top 50px right calc(50% - 100px);
  $positionRight: top 142px right calc(50% - 360px);
  $fullSize: 360px;
  $midSize: calc($fullSize / 1.2);
  $smallSize: calc($fullSize / 1.5);

  .container {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--mp-space-60);
    overflow: auto;
    position: relative;
    z-index: 0;

    &__form {
      background-color: rgba(255, 255, 255, 0.7);
      backdrop-filter: blur(25px);
      width: 100%;
      max-width: 544px;

      @include dark-mode {
        background-color: rgba(30, 30, 30, 0.7);
      }
    }

    @include screen-md {
      margin-top: var(--mp-space-100);
    }

    @keyframes toRight {
      0% {
        background-image: v-bind(pastImage);
        background-position: $positionLeft;
        background-size: $fullSize;
      }
      30% {
        background-image: v-bind(pastImage);
        background-position: $positionLeftCenter;
        background-size: $midSize;
      }
      50% {
        background-image: v-bind(currentImage);
        background-position: $positionCenter;
        background-size: $smallSize;
      }
      70% {
        background-image: v-bind(currentImage);
        background-position: $positionRightCenter;
        background-size: $midSize;
      }
      100% {
        background-image: v-bind(currentImage);
        background-position: $positionRight;
        background-size: $fullSize;
      }
    }

    @keyframes toLeft {
      0% {
        background-image: v-bind(pastImage);
        background-position: $positionRight;
        background-size: $fullSize;
      }
      30% {
        background-image: v-bind(pastImage);
        background-position: $positionRightCenter;
        background-size: $midSize;
      }
      50% {
        background-image: v-bind(currentImage);
        background-position: $positionCenter;
        background-size: $smallSize;
      }
      70% {
        background-image: v-bind(currentImage);
        background-position: $positionLeftCenter;
        background-size: $midSize;
      }
      100% {
        background-image: v-bind(currentImage);
        background-position: $positionLeft;
        background-size: $fullSize;
      }
    }
  }

  .container::before {
    z-index: -1;
    content: '';
    position: fixed;
    left: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background-repeat: no-repeat;
    animation-duration: 350ms;
    animation-timing-function: linear;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
  }

  .container {
    @include screen-lg {
      &__login::before {
        background-image: v-bind(currentImage);
        background-position: $positionRight;
        background-size: $fullSize;
      }
      &__register::before,
      &__forgotten_password::before {
        background-image: v-bind(currentImage);
        background-position: $positionLeft;
        background-size: $fullSize;
      }
      &--left::before {
        animation-name: toLeft;
      }
      &--right::before {
        animation-name: toRight;
      }
    }
  }
</style>
